import { z } from "zod";
import { GAMES, isGameId, type GameId } from "../domain/games";
import { publicUserSchema, type PublicUser } from "./profile";

/**
 * Presence + social-realtime contracts, shared by the API (publisher), the
 * game-server presence room (subscriber + fan-out), and the web client (reader).
 *
 * Two id spaces meet here, so this is the single place they are reconciled:
 * every id below is the **app** user id (cuid, `User.id`) — the same id the
 * friends API speaks (`FriendView.userId`, `affectedUserIds`) — NOT the Telegram
 * id the game rooms key their sockets on. The presence room resolves initData →
 * app user id on join so the whole pipeline stays in this one id space.
 */

/** The Colyseus room every user joins once on app open. */
export const PRESENCE_ROOM = "presence";

/**
 * Friend-graph events pushed live (mirror the friends API mutations).
 * `new_follower` fires only on a follow CREATE (re-follows are idempotent
 * no-ops); unfollow is deliberately silent.
 */
export const FRIEND_GRAPH_EVENT_TYPES = [
  "friend_request",
  "friend_accept",
  "new_follower",
] as const;
export type FriendGraphEventType = (typeof FRIEND_GRAPH_EVENT_TYPES)[number];

/**
 * Game-invite events pushed live. Each targets ONE side of the invite:
 * - `game_invite`      → the invitee (a new invite landed).
 * - `invite_accepted`  → the inviter (their invite was accepted).
 * - `invite_declined`  → the inviter (their invite was declined).
 * - `invite_canceled`  → the invitee (the inviter withdrew it — drop it live).
 * - `invite_expired`   → the inviter (the 60s window lapsed; fired when lazy
 *   expiry actually flips the row, so at most once per invite).
 */
export const INVITE_EVENT_TYPES = [
  "game_invite",
  "invite_accepted",
  "invite_declined",
  "invite_canceled",
  "invite_expired",
] as const;
export type InviteEventType = (typeof INVITE_EVENT_TYPES)[number];

/**
 * Direct-message events pushed live. `dm_message` targets the RECIPIENT of a
 * DM whose thread they do NOT currently have open (the game-server's DM room
 * publishes it exactly then; an open thread receives the message over its own
 * socket and is marked read server-side instead). `actor` is the sender — its
 * `userId` keys the client's per-thread unread counter and its `name` is
 * resolved by the shared display-name priority (never a Telegram @username).
 */
export const DM_EVENT_TYPES = ["dm_message"] as const;
export type DmEventType = (typeof DM_EVENT_TYPES)[number];

/**
 * Table-lifecycle events targeting a specific player. `game_replaced` tells a player
 * who had LEFT an online seat (still reclaimable) that the owner has filled that chair
 * with a friend — the match is over for them, so their client clears the saved
 * «ادامه بازی» game and shows a notice. `room` (below) names which game/room to drop.
 */
export const TABLE_EVENT_TYPES = ["game_replaced"] as const;
export type TableEventType = (typeof TABLE_EVENT_TYPES)[number];

/** Every event kind carried on the {@link FRIEND_EVENTS_CHANNEL}. */
export const FRIEND_EVENT_TYPES = [
  ...FRIEND_GRAPH_EVENT_TYPES,
  ...INVITE_EVENT_TYPES,
  ...DM_EVENT_TYPES,
  ...TABLE_EVENT_TYPES,
] as const;
export type FriendEventType = (typeof FRIEND_EVENT_TYPES)[number];

/**
 * Redis pub/sub channel carrying social events from the API to the presence
 * room. Historically friend-graph only; it now carries game-invite events too
 * (the name is kept for wire compatibility — routing is identical: deliver to
 * `targetUserId`'s own sockets).
 */
export const FRIEND_EVENTS_CHANNEL = "friend-events";

/**
 * The invite payload riding every {@link INVITE_EVENT_TYPES} event: enough for
 * the client to render a toast and act — accept via `POST /invites/:id/accept`,
 * or drop the matching row from its lists. `expiresAt` (ISO) lets the invitee
 * show a live countdown.
 */
export const inviteEventPayloadSchema = z.object({
  id: z.string(),
  game: z.enum(GAMES),
  roomId: z.string(),
  expiresAt: z.string(),
});
export type InviteEventPayload = z.infer<typeof inviteEventPayloadSchema>;

/**
 * The `friend-events` Redis payload (API → presence room). `targetUserId` is the
 * app user id of the person to notify; `actor` is who triggered it (for invite
 * events: the OTHER party — inviter on `game_invite`/`invite_canceled`, invitee
 * on the rest). `invite` is present exactly on the invite kinds. Validated with
 * zod on receipt — the channel is internal but untyped over the wire.
 */
export const friendEventSchema = z.object({
  targetUserId: z.string(),
  type: z.enum(FRIEND_EVENT_TYPES),
  actor: z.object({ userId: z.string(), name: z.string() }),
  invite: inviteEventPayloadSchema.optional(),
  /** Present on {@link TABLE_EVENT_TYPES} (`game_replaced`): which online game/room the
   *  target should drop from its «ادامه بازی» strip. */
  room: z.object({ game: z.enum(GAMES), roomId: z.string() }).optional(),
});
export type FriendEvent = z.infer<typeof friendEventSchema>;

/** A user's live presence. Absence from a snapshot (or an `offline` update) = offline. */
export const PRESENCE_STATUSES = ["online", "in_game"] as const;
export type PresenceStatus = (typeof PRESENCE_STATUSES)[number];

/** One friend's presence (app user id + status). */
export interface PresenceEntry {
  userId: string;
  status: PresenceStatus;
}

/**
 * Sent to a client on join: the presence of every friend currently online.
 *
 * `dataEvents` (ADDITIVE, and **load-bearing for deploy safety**) — «this server can push
 * {@link PRESENCE_MSG.dataChanged}, right now». The client slows its badge poll from 20 s to
 * {@link BADGE_POLL_SAFETY_NET_MS} ONLY when it has seen this true, because the whole basis for
 * slowing down is that the pushes arrive.
 *
 * ⚠️ IT ANSWERS «CAN THIS SERVER PUSH», NOT «IS THIS SERVER NEW». Three states make it false and
 * every one of them means the same thing to the client — there are no pushes, keep polling fast:
 *  • a game-server from BEFORE the `data-events` batch omits the field entirely (`undefined`);
 *  • a game-server with no `REDIS_URL` has no subscriber at all;
 *  • a subscriber whose channel is not currently subscribed — a Redis blip mid-`keepSubscribed`
 *    (which sets `ok = false` on every ioredis `ready` and re-subscribes), or the brief BOOT RACE:
 *    `PresenceRoom.onCreate` is SYNCHRONOUS and `keepSubscribed` subscribes in the background, so
 *    the very first joiner after a game-server recreate — the one whose join CREATES the room —
 *    can be served the snapshot before the subscribe resolves.
 *
 * ⚠️ THAT RACE IS LEFT OPEN ON PURPOSE, BECAUSE IT FAILS SAFE AND THE FIX WOULD NOT. Losing it
 * costs one client the 20 s cadence it had before this batch for one session (~114 extra requests
 * on a median 12.7 min session, once per game-server deploy) and it self-heals on that client's
 * next reconnect. Closing it by AWAITING the subscribe in `onCreate` would make the presence room
 * — the friend dots, the online counter, every live push — unable to exist at all while Redis is
 * down, which is a far worse failure than polling too often.
 * The first is what makes the deploy ORDER-INDEPENDENT: a new bundle meeting an old server —
 * a game-server-only rollback, or a push taken out of order — falls back to exactly the 20 s
 * cadence that shipped before, instead of waiting up to 120 s for pushes nobody is sending.
 */
export interface PresenceSnapshot {
  friends: PresenceEntry[];
  dataEvents?: boolean;
}

/** A single friend's presence change; `"offline"` means they went offline. */
export interface PresenceUpdate {
  userId: string;
  status: PresenceStatus | "offline";
}

/**
 * The live social event delivered to the target's own presence connection.
 * `invite` is set exactly when `type` is one of {@link INVITE_EVENT_TYPES}.
 */
export interface FriendEventPush {
  type: FriendEventType;
  actor: { userId: string; name: string };
  invite?: InviteEventPayload;
  /** Set on `game_replaced`: the online game/room to drop from the resume strip. */
  room?: { game: GameId; roomId: string };
}

/**
 * Colyseus message names on the presence socket. Server → client:
 * `snapshot`/`presence`/`friendEvent`/`gameCounts`; client → server:
 * `setActivity` (the in-game ping). Kept here so the room, its controller, and
 * the web connector never drift on a string literal.
 */
export const PRESENCE_MSG = {
  snapshot: "snapshot",
  presence: "presence",
  friendEvent: "friend-event",
  setActivity: "set-activity",
  /**
   * APPEND-ONLY: client → server «the app is (not) in front of me right now».
   *
   * The «in the app» marker the API reads before sending a Telegram PV used to track the
   * SOCKET, and a backgrounded WebView keeps its socket alive — so an invite arriving while
   * the phone was locked was pushed to a screen nobody was looking at AND had its PV
   * suppressed, i.e. it reached the user through neither channel and expired unseen. This
   * carries the client's own foreground state so the marker can track attention instead.
   */
  setForeground: "set-foreground",
  gameCounts: "game-counts",
  /** APPEND-ONLY: server → client pre-deploy maintenance warning (the apology modal). */
  maintenance: "maintenance",
  /**
   * APPEND-ONLY: server → client «one of your slices changed — re-read it».
   *
   * THE POINT IS THAT IT CARRIES NO DATA. A push that carried the new friends list, the new
   * unread counts or the new inbox would be a second encoding of three responses that already
   * exist, and every future field would have to be added twice. This says only WHICH slice
   * moved ({@link DataChangedMessage.scope}); the client re-reads the route it already had,
   * which is ETag-conditional, so an unchanged slice costs a 304 with no body.
   *
   * ⚠️ AN OLDER BUNDLE IGNORES THIS ENTIRELY — colyseus.js only dispatches messages with a
   * registered handler — which is what lets the game-server ship before the web.
   */
  dataChanged: "data-changed",
} as const;

/**
 * «مار» (Snake) — counted by presence like any game, and known to presence ONLY. It is deliberately
 * not a {@link GameId}: `GAMES` mirrors the Prisma `Game` enum and every `Record<GameId, …>` map
 * (rooms, stats, leaderboards), none of which Snake has. So it rides the same `set-activity` and
 * `game-counts` frames as one extra key, and nothing else in the platform learns its name.
 */
export const SNAKE_PRESENCE_GAME = "snake";

/**
 * «سودوکو» (Sudoku) — a solo game counted by presence exactly as «مار» is: a key presence alone
 * knows, deliberately not a {@link GameId} (it has no `Game` enum value, room, stats or leaderboard).
 */
export const SUDOKU_PRESENCE_GAME = "sudoku";

/** What `set-activity` may name and the lobby can count: every {@link GameId}, «مار» and «سودوکو». */
export type PresenceGameId = GameId | typeof SNAKE_PRESENCE_GAME | typeof SUDOKU_PRESENCE_GAME;

export function isPresenceGameId(value: string): value is PresenceGameId {
  return value === SNAKE_PRESENCE_GAME || value === SUDOKU_PRESENCE_GAME || isGameId(value);
}

/**
 * Client → server payload of the {@link PRESENCE_MSG.setActivity} ping.
 * `game` (ADDITIVE) names WHICH game the user is in when `inGame` is true —
 * it feeds the live per-game lobby counters. Legacy clients that omit it stay
 * fully functional (they count as in-game for friend dots but contribute to no
 * per-game counter); it is ignored when `inGame` is false. A game-server older
 * than «مار»'s count takes `"snake"` as no game at all — the same as a legacy ping —
 * and one older than «سودوکو»'s count takes `"sudoku"` the same way.
 */
export interface SetActivityMessage {
  inGame: boolean;
  game?: PresenceGameId | null;
}

/**
 * Live number of users currently IN-GAME per game: every {@link GameId} key, zero-filled. `snake`
 * and `sudoku` are OPTIONAL — a game-server older than that game's count omits its key, and the
 * lobby then shows no count on that tile rather than a false ۰.
 */
export type GameCounts = Record<GameId, number> & { snake?: number; sudoku?: number };

/**
 * Server → client {@link PRESENCE_MSG.gameCounts} payload. Sent to a client
 * once on join (the current snapshot) and broadcast to ALL presence sockets
 * whenever any per-game count changes (throttled server-side to ≥2s between
 * broadcasts). `online` is the TOTAL number of users connected to presence
 * (in a game or not) — the lobby top bar's live player counter; optional on
 * the wire only for the one deploy window where an older game-server omits
 * it. NOTE: both figures are PER-NODE today (presence is a single always-on
 * room per game-server process); a Redis aggregate is a documented follow-up.
 */
export interface GameCountsMessage {
  counts: GameCounts;
  online?: number;
}

/**
 * IS THIS USER IN THE APP RIGHT NOW?
 *
 * The presence room (game-server) marks each connected user with a short-lived Redis key;
 * the API reads it before sending a Telegram PV. The point: a game invite that has just
 * POPPED UP IN FRONT OF YOU in the app must not also arrive as a bot message — it is the
 * same notification twice, and the second one is noise. (The DM nudge already works this
 * way from the other side: it only fires when nobody was there to receive the push.)
 *
 * The TTL is what makes it safe: a node that dies without cleaning up leaves a marker that
 * expires on its own, so the worst case is a couple of minutes of a missing PV — never a
 * user permanently marked "online" and cut off from their notifications.
 */
/**
 * Key PREFIX for the «REACHABLE IN THE APP RIGHT NOW» marker — the PV-suppression question.
 *
 * ⚠️ THIS IS NOT THE ONLINE POPULATION. It is deliberately CLEARED while the app is
 * BACKGROUNDED and while a dropped socket is inside its reconnection grace, because in both
 * states the in-app push cannot reach anyone and the Telegram PV MUST go out. Counting these
 * keys therefore under-reports «who is online» by exactly the users with a locked phone —
 * which is what made the admin panel read lower than the number the lobby showed everyone.
 * For that question use {@link PRESENCE_SESSION_KEY_PREFIX}.
 */
export const PRESENCE_ONLINE_KEY_PREFIX = "presence:online:";

export function presenceOnlineKey(appUserId: string): string {
  return `${PRESENCE_ONLINE_KEY_PREFIX}${appUserId}`;
}

/**
 * Key PREFIX for «IS THIS USER IN THE APP» — one key per user with a live presence socket,
 * mirroring `PresenceController.online`, which is the figure the LOBBY shows every player.
 * Unlike the PV marker above it survives backgrounding and the drop grace, so a `presence:
 * session:*` count and the in-app «N نفر آنلاین» describe the SAME population.
 *
 * A SECOND KEY, never a reformatted one: the API's PV check reads the marker above and its
 * meaning must not shift under it (a backgrounded user counting as reachable again is the
 * bug that made invites vanish into a locked screen).
 */
export const PRESENCE_SESSION_KEY_PREFIX = "presence:session:";

export function presenceSessionKey(appUserId: string): string {
  return `${PRESENCE_SESSION_KEY_PREFIX}${appUserId}`;
}

/** How long a presence marker survives unrefreshed. The presence room re-arms both well
 *  inside this window while the socket is up. */
export const PRESENCE_ONLINE_TTL_SEC = 90;

/**
 * PRE-DEPLOY MAINTENANCE WARNING (deploy-safety).
 *
 * Before an operator deploys — which briefly interrupts every live online table — an admin
 * fires a warning from the bot panel. The bot publishes it on this Redis channel; the
 * game-server (subscribed) has the presence room broadcast {@link PRESENCE_MSG.maintenance}
 * to EVERY in-app socket, and the web pops an apology modal («…میزِ فعلیت از دست می‌ره…» +
 * a «متوجه شدم» button). It is a HEADS-UP ONLY — it does not disconnect anyone; the operator
 * deploys afterwards. Mirrors {@link FRIEND_EVENTS_CHANNEL}/`cache-invalidation`: a best-effort
 * Redis pub/sub channel, validated on receipt (internal but untyped over the wire).
 */
export const MAINTENANCE_CHANNEL = "maintenance";

/** The `maintenance` payload (bot → game-server → clients). `etaMinutes` is an OPTIONAL hint of
 *  how long until the deploy; the warning is still meaningful as a bare trigger (`{}`). */
export const maintenanceWarningSchema = z.object({
  etaMinutes: z.number().int().positive().optional(),
});
export type MaintenanceWarning = z.infer<typeof maintenanceWarningSchema>;

/* ------------------------------------------------------------------ *
 * DATA-CHANGED — the live path for everything the 20 s poll used to carry.
 * ------------------------------------------------------------------ */

/**
 * Which response moved. ONE value per client-side slice, deliberately coarse: the client's job
 * is «re-read that route», and the route is conditional, so a coarse scope costs a 304 rather
 * than a wasted body.
 *
 * - `friends` — `GET /friends/all` (the friends list, the incoming-request badge, outgoing).
 * - `dm`      — `GET /me/dm-summary` (unread counts, last-message previews, peers).
 * - `inbox`   — `GET /me/inbox` (the «@bing» broadcast/support feed and its badge).
 * - `session` — NOT a badge: the caller's own STANDING changed (banned/unbanned, maintenance
 *   on/off). The client re-reads `GET /me`, which is what renders the banned screen, the
 *   channel gate and the maintenance screen. It exists because those three used to be carried
 *   by the 20 s tick itself — every request runs the auth hook — so slowing the tick would
 *   have made them LESS live. Pushed, they are instant instead.
 * - `profile` — SOMEBODY ELSE's public identity changed (display name, nickname, avatar, or the
 *   official badge). ⚠️ **THE ONLY SCOPE THAT CARRIES DATA, and it carries it for a reason:**
 *   every other scope means «re-read that route», which for a profile edit would mean pulling a
 *   whole friends list to change one row's name. This one names the person and hands over the
 *   leak-free projection the client already renders, so the client patches that ONE entry in
 *   place. SILENT — no toast, no sound, no refetch — and targeted at the people who have that
 *   name on screen (accepted friends + open DM peers), never broadcast.
 */
export const DATA_SCOPES = ["friends", "dm", "inbox", "session", "profile"] as const;
export type DataScope = (typeof DATA_SCOPES)[number];

/**
 * Redis pub/sub channel carrying «this slice moved» from whoever wrote it to the presence room.
 * Publishers today: the API (friend-graph mutations, inbox writes, DM hide, profile edits, ban
 * and maintenance) and the game-server's own DM room (a thread read, and a sent message — for
 * the SENDER's other devices; the recipient already gets {@link PRESENCE_MSG.friendEvent}).
 *
 * Mirrors {@link MAINTENANCE_CHANNEL}: best-effort, internal but untyped over the wire, so it is
 * validated on receipt and a malformed payload is dropped rather than thrown.
 */
export const DATA_EVENTS_CHANNEL = "data-events";

/**
 * The `data-events` payload (publisher → game-server → clients).
 *
 * Exactly one of `targetUserIds` / `all` is meaningful: `all` is the whole-platform fan-out
 * (an in-app broadcast writes ~107k `InboxMessage` rows, and telling each recipient
 * individually would be ~107k Redis publishes — one broadcast to the presence room is one
 * encode and one write per live socket), `targetUserIds` is everyone else.
 *
 * ⚠️ `targetUserIds` is a LIST, not a single id, because the common publishers are inherently
 * plural: a friend-graph mutation affects BOTH parties, and a profile edit affects every friend
 * and every DM peer at once.
 */
export const dataEventSchema = z.object({
  scope: z.enum(DATA_SCOPES),
  targetUserIds: z.array(z.string()).optional(),
  all: z.boolean().optional(),
  /**
   * Present on `profile` ONLY: what that person's public identity now is.
   *
   * ⚠️ VALIDATED HERE RATHER THAN PASSED THROUGH. The presence room forwards it verbatim to every
   * recipient, so an unvalidated field would be an api-to-client channel with no schema — and
   * `publicUserSchema` is the same leak-free projection `/friends/all` serves, so a field that
   * must never reach another user cannot be added by accident here either.
   */
  user: publicUserSchema.optional(),
});
export type DataEvent = z.infer<typeof dataEventSchema>;

/**
 * Server → client {@link PRESENCE_MSG.dataChanged} payload — the scope, and for `profile` the
 * changed person's public identity, so the client patches one entry without re-reading a list.
 */
export interface DataChangedMessage {
  scope: DataScope;
  user?: PublicUser;
  /**
   * ADDITIVE — «this went to EVERY connected socket», i.e. a `changedForAll` fan-out.
   *
   * ⚠️ THE CLIENT NEEDS TO KNOW, BECAUSE A SYNCHRONISED REFRESH IS A DIFFERENT ANIMAL. A
   * targeted push is one client re-reading one route and must stay instant (31–162 ms measured).
   * A fan-out is every client re-reading the same route in the same millisecond, against a
   * single-threaded Fastify — and a 304 costs the server the same as a 200, because the ETag is
   * computed in `onSend` after the handler has already run. So the client spreads exactly these
   * over {@link BROADCAST_RESYNC_SPREAD_MS} and nothing else. An older bundle simply ignores the
   * field and behaves as it did.
   */
  all?: boolean;
}
